import type { AppSettingsSchema } from "@/interfaces/config/AppSettingsSchema";
import Logger from "@/utils/Logger";

/**
 * Retrieves the runtime configuration from the global `__APP_SETTINGS__` variable.
 */
const getRuntimeConfig = (): AppSettingsSchema => {
  if (typeof window !== "undefined") {
    const settings = window.__APP_SETTINGS__;
    if (!settings) {
      Logger.error("Missing runtime config - using empty config", null, [
        "AppSettingsHelper",
      ]);
      return {} as AppSettingsSchema;
    }

    if (settings?.use_internal_api_url === "true") {
      Logger.debug("Runtime config loaded (internal API mode)", [
        "AppSettingsHelper",
      ]);
    } else {
      Logger.debug("Runtime config loaded (external API mode)", [
        "AppSettingsHelper",
      ]);
    }

    return settings;
  }

  Logger.warn("Window object not available - using empty config", [
    "AppSettingsHelper",
  ]);
  return {} as AppSettingsSchema;
};

/**
 * Retrieves configuration with fallback to default values
 */
const getRuntimeConfigOrDefault = (
  defaultAppSettings: AppSettingsSchema,
): AppSettingsSchema => {
  try {
    const config = getRuntimeConfig();
    // Merge with defaults for any missing keys
    return { ...defaultAppSettings, ...config };
  } catch (error) {
    Logger.warn("Using default config due to error", ["AppSettingsHelper"]);
    return defaultAppSettings;
  }
};

/**
 * Validates that critical configuration keys are present
 */
const validateCriticalConfig = (): boolean => {
  const criticalKeys: (keyof AppSettingsSchema)[] = [
    "api_base_url",
    "openid_authority",
    "ping_client_id",
    "costco_environment",
  ];

  const config = getRuntimeConfig();
  const missingKeys = criticalKeys.filter(
    (key) => !config[key] || config[key] === undefined || config[key] === null,
  );

  if (missingKeys.length > 0) {
    const errorMessage = `Missing critical configuration: ${missingKeys.join(", ")}`;
    Logger.error(errorMessage, null, ["AppSettingsHelper"]);
    throw new Error(errorMessage);
  }

  Logger.debug("Critical configuration validation passed", [
    "AppSettingsHelper",
  ]);
  return true;
};

/**
 * Generates a configuration summary for debugging
 */
const getConfigSummary = (): Record<string, any> => {
  try {
    const config = getRuntimeConfig();

    return {
      hasApiUrl: !!config.api_base_url,
      hasApiClientIdentifier: !!config.api_client_identifier,
      hasInternalApiUrl: !!config.api_internal_url,
      useInternalApi: config.use_internal_api_url === "true",
      hasAuth: !!config.openid_authority,
      environment: config.costco_environment || "unknown",
      cloudEnv: config.cloud_environment || "unknown",
      configKeys: Object.keys(config).length,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    Logger.error("Error generating config summary", error, [
      "AppSettingsHelper",
    ]);
    return {
      error: "Failed to generate summary",
      timestamp: new Date().toISOString(),
    };
  }
};

/**
 * Gets all configuration entries as key-value pairs
 */
const getAllConfigEntries = (): Record<string, string> => {
  try {
    const config = getRuntimeConfig();
    return Object.entries(config).reduce(
      (acc, [key, value]) => {
        acc[key] = String(value || "");
        return acc;
      },
      {} as Record<string, string>,
    );
  } catch (error) {
    Logger.error("Error retrieving config entries", error, [
      "AppSettingsHelper",
    ]);
    return {};
  }
};

/**
 * Checks if a specific configuration key exists and has a value
 */
const hasConfigValue = (key: keyof AppSettingsSchema): boolean => {
  try {
    const config = getRuntimeConfig();
    const value = config[key];
    return value !== undefined && value !== null && value !== "";
  } catch {
    return false;
  }
};

/**
 * Gets a configuration value with a fallback
 */
const getConfigValueOrDefault = <T extends keyof AppSettingsSchema>(
  key: T,
  defaultValue: string,
): string => {
  try {
    const config = getRuntimeConfig();
    const value = config[key];
    return value ? String(value) : defaultValue;
  } catch (error) {
    Logger.warn(`Using default value for "${String(key)}"`, [
      "AppSettingsHelper",
    ]);
    return defaultValue;
  }
};

export const AppSettingsHelper = {
  // Core functions
  getRuntimeConfig,
  getRuntimeConfigOrDefault,

  // Validation and utilities
  validateCriticalConfig,
  getConfigSummary,
  getAllConfigEntries,
  hasConfigValue,
  getConfigValueOrDefault,
};
